import {
  type GenerateLtdResponse,
  type UpdateFormationSlotResponse,
  type InventoryVerificationEventResponse,
  type TypeCurveVolumeRecord,
} from 'types/api-responses';
import type { TypeCurveWorkspaceResponse } from 'types/type-curve-workspace';
export type { GenerateLtdResponse } from 'types/api-responses';
import { atom } from 'jotai';
import { MapUserConfig } from 'components/map/types';
export {
  pageSectionVisibilityAtom,
  probitPlotChartAtom,
  monthlyVolumesChartAtom,
  ratiosChartAtom,
  crossPlotChartAtom,
  tcMapFilterModeAtom,
  tcSpatialFilterDisabledAtom,
  tcEffectiveMapFilterModeAtom,
  tcMapRefAtom,
  tcAoiPolygonAtom,
  tcAoiPolygonLockedAtom,
  tcWellFilterModalOpenAtom,
  tcWellFiltersAtom,
  tcPendingWellFiltersAtom,
  tcColorByModalOpenAtom,
  tcColorByConfigAtom,
  tcColorByRulesAtom,
  tcPendingColorByAtom,
  tcWellsDataAtom,
  tcWellsFacetsAtom,
  tcColorByUwiMapAtom,
  tcColorByLoadingAtom,
  tcMapBoundsAtom,
  tcSelectedDevAreaAtom,
  tcSelectDevAreaRequestAtom,
} from './type-curve-filters/atoms';
export type { TcSelectedDevArea } from './type-curve-filters/atoms';
import type { MineralType } from 'types/minerals';
import { createMutationAtom } from 'atoms/create-resource-atom';
import type { TypeCurveProbitResponse } from 'types/type-curve-probit';
import type { GunBarrelSlotItem } from 'components/charts/gun-barrel-chart/types';
import type {
  UnitVerification,
  InventoryVerificationSummary,
  TypeCurveDraft,
} from './types';

// ─── Per-stream aggregation ─────────────────────────────────────────────────

/** Per-stream aggregation selection (streamId → aggregation type, camelCase).
 *  Shared between the main-page volume charts and the DCA modal so they stay
 *  in sync. Defaults to 'p50' when a stream has no explicit entry. */
export const tcStreamAggregationAtom = atom<Record<string, string>>({});

// ─── Type Curve Details Draft ───────────────────────────────────────────────

/** Draft state for the editable Type Curve Details form in not-promoted workspaces. */
export const tcDetailsDraftAtom = atom<TypeCurveDraft | null>(null);

// ─── Inventory Workspace area context ───────────────────────────────────────

/** The Type Curve area (by name) highlighted on the Inventory Workspace map, or
 *  null for none. Shared between the areas layer (inside `<Map>`) and the
 *  "Inventory context" legend overlay (a sibling), so the legend can drive the
 *  highlight without threading state through `TypeCurveMap`. */
export const tcInventoryHighlightedAreaAtom = atom<string | null>(null);

// Story 1: createTypeCurveHeadersSearchAtom removed — listing page now uses
// React Query hooks (use-type-curves-listing.ts). TypeCurveHeadersQueryParams
// is kept as a type alias so the export hook (hooks.ts) still compiles.
import type { TypeCurvesListingFilters } from './use-type-curves-listing';
export type TypeCurveHeadersQueryParams = TypeCurvesListingFilters;
export type TypeCurveHeadersFacets = Record<string, unknown>;

export const scopedWellsVisibilityAtom = atom(true);

/**
 * The "Type Curve" map group: one toggle per child.
 *
 * `typeCurveWells` covers all three well categories at once — Well Control,
 * Analog Wells and New Wells are no longer separately toggleable, so the layer
 * reads this single value rather than one atom each.
 */
export const typeCurveWellsVisibilityAtom = atom(true);
/** LTD laterals (`tc-slot-geometries-layer`). */
export const ltdPropertiesVisibilityAtom = atom(true);
/** Unit polygons, outlines and labels (`tc-development-areas-layer`). */
export const typeCurveUnitsVisibilityAtom = atom(true);
/**
 * The workspace's type-curve area footprints (`tc-inventory-areas-layer`) — the
 * launched area and its co-developed neighbours.
 */
export const typeCurveAreaVisibilityAtom = atom(true);

export const queryGroupFilterAtom = atom<string | null>(null);
export const queryGroupVisibilityAtom = atom({
  new_wells_not_in_control: true,
  wells_in_type_curve_area_not_in_control: true,
  wells_in_control: true,
});

export const mapFilterAtom = atom({
  wellControl: true,
  wellsInArea: true,
  newWells: true,
});

export const mapConfigAtom = atom<MapUserConfig>({
  wells: {
    checked: false,
    options: {
      surfaceHole: false,
      bottomHole: false,
      stick: false,
      survey: false,
      vertical: false,
    },
  },
  wellsV2: {
    checked: false,
    options: {
      vertical: false,
      horizontal: false,
      horizontalPermit: false,
      horizontalDuc: false,
      horizontalProducing: false,
      horizontalShutIn: false,
      horizontalExpiredPermit: false,
      horizontalInactive: false,
      horizontalPrePermit: false,
      horizontalApd: false,
      horizontalProposal: false,
      horizontalPlanned: false,
      verticalPermit: false,
      verticalDuc: false,
      verticalProducing: false,
      verticalShutIn: false,
      verticalExpiredPermit: false,
      verticalInactive: false,
      verticalPrePermit: false,
      verticalApd: false,
      verticalProposal: false,
      verticalPlanned: false,
      surfaceHole: false,
      bottomHole: false,
      stick: false,
      survey: false,
      permit: false,
      duc: false,
      producing: false,
      shutIn: false,
      expiredPermit: false,
      inactive: false,
      prePermit: false,
      apd: false,
      proposal: false,
      planned: false,
      showWellNames: false,
    },
  },
  developmentAreas: {
    checked: false,
    options: {
      horizontalUnits: false,
      verticalUnits: false,
      fieldUnits: false,
      units: false,
      pricing: false,
    },
  },
  basinTier: {
    checked: false,
    options: {
      basin: false,
      tier: false,
    },
  },
  typeCurves: { checked: false },
  scrollZoom: {
    checked: false,
  },
  popups: {
    checked: true,
  },
  focusAreas: {
    checked: false,
    selections: {},
  },
  titleEntityAssets: {
    checked: false,
    options: {
      surfaceInterest: false,
      workingInterest: false,
      unleased: false,
      royaltyInterest: false,
    },
  },
  portfolioAssets: {
    checked: false,
  },
  pipelines: {
    checked: false,
    options: {
      gasOperating: false,
      gasUnderConstruction: false,
      gasPlannedOther: false,
      oilOperating: false,
      oilUnderConstruction: false,
      oilPlannedOther: false,
      productOperating: false,
      productConstruction: false,
      productPlannedOther: false,
      unknownOperating: false,
      unknownUnderConstruction: false,
      unknownPlannedOther: false,
    },
  },
  drillingRigs: {
    checked: false,
    options: {
      horizontalDrilling: false,
      horizontalDown: false,
      horizontalNotReporting: false,
      horizontalNotTracking: false,
      horizontalUnreportedStatus: false,
      verticalDrilling: false,
      verticalDown: false,
      verticalNotReporting: false,
      verticalNotTracking: false,
      verticalUnreportedStatus: false,
    },
  },
});

export const toastAtom = atom<{
  kind: 'error' | 'success' | 'info';
  title: string;
  subtitle?: string;
  timeout?: number;
} | null>(null);

// One mutation atom per stream so each probit card owns an independent
// request/loading/result lifecycle. We wrap the generic factory in a
// non-generic helper so its return type is easy to name via
// `ReturnType<typeof …>` — `typeof createMutationAtom<…>` can't be used
// directly in a type position, and `satisfies` isn't available on the
// parser version used by the repo's lint config.
export const createTypeCurveProbitMutationAtom = () =>
  createMutationAtom<TypeCurveProbitResponse, 'typeCurveProbit'>();

export const typeCurveProbitMutationAtoms: Record<
  MineralType,
  ReturnType<typeof createTypeCurveProbitMutationAtom>
> = {
  oil: createTypeCurveProbitMutationAtom(),
  gas: createTypeCurveProbitMutationAtom(),
  water: createTypeCurveProbitMutationAtom(),
};

export const TYPE_CURVE_PROBIT_URL = '/queries/probit_plot';

/**
 * Per-stream key of the last probit dispatch payload. Shared across all
 * components (main page + DCA modal) so that when one component already
 * dispatched with a given payload, others can skip the redundant call.
 * Module-level (not an atom) because we only need mutable storage, not
 * reactivity — consumers compare synchronously inside useEffect.
 */
export const probitLastDispatchKey: Partial<Record<MineralType, string>> = {};

// Bumped after Generate LTD Inventory re-lays a unit.
export const ltdRefreshTriggerAtom = atom(0);

// Bumped after Update Geometries persists a slot move/trim. Kept as its OWN
// trigger (separate from LTD) purely so each action is named at the point it
// fires — the two now drive the SAME fan-out, via `geometryRefreshAtom` below.
// The seam is retained in case they ever need to diverge again. Renamed from
// `spacingRefreshTriggerAtom`.
export const updateGeometriesRefreshTriggerAtom = atom(0);

// The unified geometry-refresh signal both inventory actions drive: every
// per-surface data fetch (gun barrel, slot table, spacing configs, map slot
// geometry), the dev-area MVT cache-bust AND the inventory-package api subscribe
// to THIS, so Generate LTD and Update Geometries each refetch all of them on BOTH
// surfaces (main + builder). Both actions re-resolve the zone's inventory details
// — one persists a slot move/trim, the other re-lays the unit's slots — so
// neither is an exception any more.
// Bumped after a slot's valued/notes is persisted. Deliberately NOT part of
// `geometryRefreshAtom`: a valuation change moves nothing, so the spacing
// configs, map slot geometry, dev-area tiles and inventory-package api have
// nothing to re-read. Only the two fetches that carry the flag subscribe — the
// gun barrel and the slot table — which also keeps `serverSlots` current, so a
// second right-click sees the saved value rather than a stale one.
export const slotValuationRefreshTriggerAtom = atom(0);

// Bumped after a composite wellbore is merged or split. Not part of
// `geometryRefreshAtom`: only the map slot listing, gun barrel, slot table and
// composites index carry the composite keys, so only they subscribe.
export const compositeRefreshTriggerAtom = atom(0);

export const geometryRefreshAtom = atom(
  (get) => get(ltdRefreshTriggerAtom) + get(updateGeometriesRefreshTriggerAtom)
);

export const probitRefreshTriggerAtom = atom(0);

export const generateLtdMutationAtom = createMutationAtom<
  GenerateLtdResponse,
  'generateLtdInventoryGeometries'
>();

export const generateUnitLtdMutationAtom = createMutationAtom<
  GenerateLtdResponse,
  'generateUnitLtdInventoryGeometries'
>();

/** Selected slot for the notes panel. Null = panel closed. */
export const selectedSlotAtom = atom<GunBarrelSlotItem | null>(null);

/** Write-only atom to close the panel. */
export const closeSlotPanelAtom = atom(null, (_get, set) => {
  set(selectedSlotAtom, null);
});

/** Slot valued/notes overrides applied optimistically on the FE.
 *  Keyed by slotId. Consumed by the gun barrel chart (cross icons) and the
 *  map layer (hide geometry for not-valued slots). */
export const slotOverridesAtom = atom<
  Map<number, { valued: boolean; notes: string | null }>
>(new Map());

/** Mutation atom for PATCH formation_slots. */
export const updateFormationSlotMutationAtom = createMutationAtom<
  UpdateFormationSlotResponse,
  'updateFormationSlot'
>();

// ─── Inventory Verification ─────────────────────────────────────────────────

/** Verification data for all units in the active workspace, keyed by unitName. */
export const unitVerificationsAtom = atom<Map<string, UnitVerification>>(
  new Map()
);

/** Aggregated inventory verification summary from the summary API. */
export const inventoryVerificationSummaryAtom =
  atom<InventoryVerificationSummary | null>(null);

/** Mutation atom for POST /verification_items/:id/events. */
export const inventoryVerificationEventMutationAtom = createMutationAtom<
  InventoryVerificationEventResponse,
  'inventoryVerificationEvent'
>();

// Story 1 — top-level workspace creation (POST /type_curve_workspaces)
export const createTopLevelTcWorkspaceMutationAtom = createMutationAtom<
  TypeCurveWorkspaceResponse,
  'createTypeCurveWorkspace'
>();

// ─── Shared Chart Data (main page → DCA modal) ─────────────────────────────
//
// These atoms hold the main page's fetched chart data so the DCA modal can
// open instantly without re-fetching. Each data source has a separate atom
// because they update at different cadences (production wells change with
// UWIs, aggregation accumulates incrementally, TC data changes with scenario).

import type { MultiWellProductionData } from 'components/charts/multi-well-volume-chart';
import type {
  TypeCurveVolumes,
  AggregationCache,
  WellCountData,
} from './hooks';

export const tcProductionWellsAtom = atom<MultiWellProductionData[]>([]);
export const tcTypeCurveVolumesAtom = atom<TypeCurveVolumes | null>(null);
export const tcAggregationCacheAtom = atom<AggregationCache>({});
export const tcWellCountsAtom = atom<WellCountData | null>(null);

// ─── Forecast Override (for Probit) ─────────────────────────────────────────

/** Forecast-derived data from the TC DCA modal, used by probit charts. */
export interface TcForecastOverride {
  /** The header details ID that produced this data — only used when it matches the current TC */
  typeCurveHeaderDetailsId: number;
  eur: { oil: number; gas: number; water: number } | null;
  eurRatios: Record<string, number> | null;
  cumulatives: TypeCurveVolumeRecord[];
  /** DCA params per stream — keys are snake_case to match probit paramKey */
  dcaParams: Record<
    string,
    {
      qi: number | null;
      di: number | null;
      b_factor: number | null;
      dlim: number | null;
    } | null
  >;
}

export const tcForecastOverrideAtom = atom<TcForecastOverride | null>(null);

/** TC lateral length (ft) from the effective header. Used for EUR/ft conversion. */
export const tcLateralLengthFtAtom = atom<number | null>(null);

/** Per-stream probit result override, shared between main page and DCA modal.
 *  Main page writes here after each successful fetch; DCA modal writes on save.
 *  Each response self-describes its metric, so consumers can check
 *  `override.metric === currentMetric` before using the data.
 *  Mirrors the tcProductionWellsAtom / tcDcaForecastVolumesAtom pattern. */
type ProbitOverrideAtom = ReturnType<
  typeof atom<TypeCurveProbitResponse | null>
>;

export const tcProbitOverrideAtoms: Record<MineralType, ProbitOverrideAtom> = {
  oil: atom<TypeCurveProbitResponse | null>(null),
  gas: atom<TypeCurveProbitResponse | null>(null),
  water: atom<TypeCurveProbitResponse | null>(null),
};

// The single Type Curve export moved to the async server-export flow
// (useTypeCurveDetailExport in ./hooks), so the workbook's DCA forecast no longer
// blocks the web request. See INS-11713.
